import { zodResolver } from "@hookform/resolvers/zod";
import { AlertBar, Icons } from "@win-reality/react-ui";
import { useCallback, useEffect, useRef } from "react";
import { type DefaultValues, useForm } from "react-hook-form";

import { StrikeZone } from "@/components/strikeZone/StrikeZone";
import {
  Field,
  type FieldDimensions,
  type FieldValues,
} from "@/components/ui/FieldConfiguration/Field";
import {
  DIMENSIONS_BY_SPORT,
  MOUND_DISTANCE_BY_SPORT,
} from "@/components/ui/FieldConfiguration/fieldDefaults";

import {
  configurationFormSchema,
  type ConfigurationFormValues,
} from "./EventForm.schema";
import { EventLEDBoardConfiguration } from "./EventLEDBoardConfiguration/EventLEDBoardConfiguration";

const FORM_OPTS = { shouldDirty: true, shouldValidate: true } as const;

// When the form initializes with a sport already chosen (e.g. from the field's
// current config), backfill any dimension that came through as 0 with the
// sport-based default. Zero isn't a meaningful field size, so we treat it as
// "unset" rather than as an explicit user value.
function withSportDefaults(
  values: DefaultValues<ConfigurationFormValues>,
): DefaultValues<ConfigurationFormValues> {
  const sport = values.sport;
  if (sport !== "baseball" && sport !== "softball") return values;
  const dims = DIMENSIONS_BY_SPORT[sport];
  const mound = MOUND_DISTANCE_BY_SPORT[sport];
  return {
    ...values,
    moundDistance: values.moundDistance || mound,
    baseDistance: values.baseDistance || dims.diamondSizeFeet,
    infieldAtFoulLine: values.infieldAtFoulLine || dims.infieldAtFoulLineFeet,
    fenceFirstBase: values.fenceFirstBase || dims.fenceFirstBaseFeet,
    fenceThirdBase: values.fenceThirdBase || dims.fenceThirdBaseFeet,
    leftField: values.leftField || dims.fenceLeftFieldFeet,
    centerField: values.centerField || dims.fenceCenterFieldFeet,
    rightField: values.rightField || dims.fenceRightFieldFeet,
  };
}

type ConfigurationProps = {
  isFieldHealthy: boolean;
  fieldName: string;
  onValidChange?: (isValid: boolean) => void;
  onDirtyChange?: (isDirty: boolean) => void;
  onValuesChange?: (values: ConfigurationFormValues) => void;
  initialValues?: Partial<ConfigurationFormValues>;
  isLive?: boolean;
  isEditing?: boolean;
};

function EventConfiguration({
  isFieldHealthy,
  fieldName,
  onValidChange,
  onDirtyChange,
  onValuesChange,
  initialValues,
  isLive = false,
  isEditing = false,
}: ConfigurationProps) {
  const form = useForm<ConfigurationFormValues>({
    resolver: zodResolver(configurationFormSchema),
    mode: "onChange",
    defaultValues: withSportDefaults({
      ledBoardMode: "stat_tracking",
      sport: undefined,
      level: undefined,
      moundDistance: 0,
      baseDistance: 0,
      infieldAtFoulLine: 0,
      fenceFirstBase: 0,
      fenceThirdBase: 0,
      leftField: 0,
      centerField: 0,
      rightField: 0,
      ...initialValues,
    }),
  });

  const isValid = form.formState.isValid;
  const isDirty = form.formState.isDirty;
  const values = form.watch();

  // Validate initial values on mount if they exist
  useEffect(() => {
    if (initialValues && Object.keys(initialValues).length > 0) {
      form.trigger();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    onValidChange?.(isValid);
  }, [isValid, onValidChange]);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const prevValuesJson = useRef<string>("");
  useEffect(() => {
    if (isValid) {
      const json = JSON.stringify(values);
      if (json !== prevValuesJson.current) {
        prevValuesJson.current = json;
        onValuesChange?.(values);
      }
    }
  }, [values, isValid, onValuesChange]);

  const handleLedBoardChange = useCallback(
    (value: ConfigurationFormValues["ledBoardMode"]) =>
      form.setValue("ledBoardMode", value, FORM_OPTS),
    [form],
  );

  const handleSportChange = useCallback(
    (sport: FieldValues["sport"]) => {
      form.setValue("sport", sport!, FORM_OPTS);
      const defaults = sport ? DIMENSIONS_BY_SPORT[sport] : undefined;
      const moundDistance =
        sport && sport in MOUND_DISTANCE_BY_SPORT
          ? MOUND_DISTANCE_BY_SPORT[sport as "baseball" | "softball"]
          : undefined;
      if (defaults) {
        form.setValue("baseDistance", defaults.diamondSizeFeet, FORM_OPTS);
        form.setValue(
          "infieldAtFoulLine",
          defaults.infieldAtFoulLineFeet,
          FORM_OPTS,
        );
        form.setValue("fenceFirstBase", defaults.fenceFirstBaseFeet, FORM_OPTS);
        form.setValue("fenceThirdBase", defaults.fenceThirdBaseFeet, FORM_OPTS);
        form.setValue("leftField", defaults.fenceLeftFieldFeet, FORM_OPTS);
        form.setValue("centerField", defaults.fenceCenterFieldFeet, FORM_OPTS);
        form.setValue("rightField", defaults.fenceRightFieldFeet, FORM_OPTS);
      }
      if (moundDistance !== undefined) {
        form.setValue("moundDistance", moundDistance, FORM_OPTS);
      }
    },
    [form],
  );

  const handleLevelChange = useCallback(
    (level: FieldValues["level"]) => form.setValue("level", level!, FORM_OPTS),
    [form],
  );

  const handleMoundDistanceChange = useCallback(
    (moundDistance: number) =>
      form.setValue("moundDistance", moundDistance, FORM_OPTS),
    [form],
  );

  const handleDimensionsSave = useCallback(
    (dims: FieldDimensions) => {
      if (dims.diamondSizeFeet !== undefined)
        form.setValue("baseDistance", dims.diamondSizeFeet, FORM_OPTS);
      if (dims.infieldAtFoulLineFeet !== undefined)
        form.setValue(
          "infieldAtFoulLine",
          dims.infieldAtFoulLineFeet,
          FORM_OPTS,
        );
      if (dims.fenceFirstBaseFeet !== undefined)
        form.setValue("fenceFirstBase", dims.fenceFirstBaseFeet, FORM_OPTS);
      if (dims.fenceThirdBaseFeet !== undefined)
        form.setValue("fenceThirdBase", dims.fenceThirdBaseFeet, FORM_OPTS);
      if (dims.fenceLeftFieldFeet !== undefined)
        form.setValue("leftField", dims.fenceLeftFieldFeet, FORM_OPTS);
      if (dims.fenceCenterFieldFeet !== undefined)
        form.setValue("centerField", dims.fenceCenterFieldFeet, FORM_OPTS);
      if (dims.fenceRightFieldFeet !== undefined)
        form.setValue("rightField", dims.fenceRightFieldFeet, FORM_OPTS);
    },
    [form],
  );

  const handleStrikeZoneChange = useCallback(
    (config: ConfigurationFormValues["strikeZone"]) => {
      form.setValue("strikeZone", config, FORM_OPTS);
    },
    [form],
  );

  const fieldValues: FieldValues = {
    sport: values.sport,
    level: values.level,
    rubberDistanceF: values.moundDistance,
    diamondSizeFeet: values.baseDistance ?? 0,
    infieldAtFoulLineFeet: values.infieldAtFoulLine ?? 0,
    fenceFirstBaseFeet: values.fenceFirstBase ?? 0,
    fenceThirdBaseFeet: values.fenceThirdBase ?? 0,
    fenceLeftFieldFeet: values.leftField ?? 0,
    fenceCenterFieldFeet: values.centerField ?? 0,
    fenceRightFieldFeet: values.rightField ?? 0,
  };

  return (
    <div className="flex flex-col gap-8">
      <div className="max-w-128 mx-auto w-full">
        <div className="text-base font-bold my-4">
          {isEditing ? "Configurations" : "Step #2: Configurations"}
        </div>

        <AlertBar
          icon={<Icons.InfoIconOutline className="size-5" />}
          variant="info"
        >
          {isLive
            ? `These presets will apply to ${fieldName} and become active as soon as you start. They will persist until overridden manually in the field configuration or by a future scheduled event.`
            : `These presets will apply to ${fieldName} and become active at the start time of this event. They will persist until overridden manually in the field configuration or by a future scheduled event.`}
        </AlertBar>
      </div>

      <div className="max-w-128 w-full mx-auto">
        <EventLEDBoardConfiguration
          isFieldHealthy={isFieldHealthy}
          value={values.ledBoardMode}
          onValueChange={handleLedBoardChange}
        />
      </div>

      <div className="max-w-128 w-full mx-auto">
        <Field
          values={fieldValues}
          onSportChange={handleSportChange}
          onLevelChange={handleLevelChange}
          onMoundDistanceChange={handleMoundDistanceChange}
          onDimensionsSave={handleDimensionsSave}
        />
      </div>

      <div className="max-w-128 w-full mx-auto mb-4">
        <StrikeZone
          sport={values.sport ?? "baseball"}
          values={values.strikeZone ?? null}
          onChange={handleStrikeZoneChange}
        />
      </div>
    </div>
  );
}

export { EventConfiguration };
